import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, LogOut, Ticket, Users, Award, Loader2, Home, LayoutDashboard, Receipt, UserPlus, ArrowRight, Video } from "lucide-react";
import { authHeaders } from "@/components/schulungen/roles";
import KontaktKarte from "@/components/schulungen/KontaktKarte";
import FirmenBestellungen from "@/components/schulungen/FirmenBestellungen";
import FirmenEinladungen from "@/components/schulungen/FirmenEinladungen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200/60 rounded-2xl p-6 flex items-center gap-4" data-testid={testId}>
      <div className="h-12 w-12 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center shrink-0"><Icon className="h-6 w-6" /></div>
      <div><p className="text-sm text-slate-500">{label}</p><p className="font-['Manrope'] text-2xl font-bold text-slate-900">{value}</p></div>
    </div>
  );
}

function Uebersicht({ firma, teilnehmer, goTo }) {
  const teilnehmerCount = teilnehmer.length;
  const zertifikate = teilnehmer.filter((t) => t.summary?.zertifikat).length;
  const steps = [
    { icon: Receipt, title: "Schulungsplätze bestellen", desc: "180 € pro Platz, Zahlung auf Rechnung – Plätze sind sofort verfügbar.", tab: "bestellungen", done: firma.plaetze_gesamt > 0 },
    { icon: UserPlus, title: "Mitarbeitende einladen", desc: "Pro freiem Platz einen persönlichen Einladungslink erzeugen und selbst weiterleiten.", tab: "mitarbeiter", done: teilnehmerCount > 0 },
    { icon: Video, title: "Online-Präsenztermin (freiwillig)", desc: "Ihre Mitarbeitenden buchen den freiwilligen Praxistag – er findet online als Video-Seminar statt.", tab: "mitarbeiter", done: teilnehmer.some((t) => t.summary?.termin) },
    { icon: Award, title: "Rechnung begleichen – Zertifikat per Post", desc: "Nach Zahlungseingang und bestandenem Test stellen wir das Zertifikat aus und senden es per Post an Ihre Firmenadresse.", tab: "bestellungen", done: teilnehmer.some((t) => t.summary?.zertifikat) },
  ];
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard icon={Ticket} label="Freie Plätze" value={`${firma.plaetze_frei ?? 0} / ${firma.plaetze_gesamt ?? 0}`} testId="stat-plaetze-frei" />
        <StatCard icon={Users} label="Teilnehmende" value={teilnehmerCount} testId="stat-teilnehmer" />
        <StatCard icon={Award} label="Zertifikate" value={zertifikate} testId="stat-zertifikate" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200/60 rounded-2xl p-6 sm:p-8">
          <h2 className="font-['Manrope'] text-lg font-bold text-slate-800">So geht es weiter</h2>
          <ol className="mt-5 space-y-3">
            {steps.map((s, i) => (
              <li key={s.title}>
                <button onClick={() => goTo(s.tab)} className="w-full text-left flex gap-4 p-3 -mx-3 rounded-xl hover:bg-slate-50 transition-colors group" data-testid={`step-${i + 1}`}>
                  <div className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center font-bold font-['Manrope'] ${s.done ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}`}>{i + 1}</div>
                  <div className="flex-1"><p className="font-semibold text-slate-800">{s.title}</p><p className="text-sm text-slate-600">{s.desc}</p></div>
                  <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-cyan-700 self-center" />
                </button>
              </li>
            ))}
          </ol>
        </div>
        <div className="space-y-6">
          <KontaktKarte firmenname={firma.firmenname} />
          <div className="bg-white border border-slate-200/60 rounded-2xl p-6" data-testid="portal-firmendaten">
            <h2 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4">Firmendaten</h2>
            <dl className="space-y-3 text-sm">
              <div><dt className="text-slate-500">Firma</dt><dd className="font-medium text-slate-800">{firma.firmenname}</dd></div>
              <div><dt className="text-slate-500">Ansprechpartner/in</dt><dd className="font-medium text-slate-800">{firma.ansprechpartner}</dd></div>
              <div><dt className="text-slate-500">E-Mail</dt><dd className="font-medium text-slate-800 break-all">{firma.email}</dd></div>
              {firma.telefon && <div><dt className="text-slate-500">Telefon</dt><dd className="font-medium text-slate-800">{firma.telefon}</dd></div>}
              {(firma.strasse || firma.ort) && <div><dt className="text-slate-500">Adresse</dt><dd className="font-medium text-slate-800">{firma.strasse}{firma.strasse && <br />}{firma.plz} {firma.ort}</dd></div>}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FirmenPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "uebersicht";
  const [firma, setFirma] = useState(null);
  const [teilnehmer, setTeilnehmer] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/schulungen/firmen/me`, authHeaders(user)),
      axios.get(`${API}/schulungen/teilnehmer`, authHeaders(user)),
    ]).then(([f, t]) => { setFirma(f.data); setTeilnehmer(t.data); })
      .catch(() => toast.error("Firmendaten konnten nicht geladen werden"));
  }, [user]);

  useEffect(() => { document.title = "Firmenportal | Schulungen | Lehnen & Weinacht"; load(); }, [load]);

  const goTo = (t) => setParams(t === "uebersicht" ? {} : { tab: t });
  const handleLogout = () => { logout(); navigate("/schulungen/login"); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="firmen-portal">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-cyan-700 text-white flex items-center justify-center shrink-0"><Building2 className="h-5 w-5" /></div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800 font-['Manrope'] truncate" data-testid="portal-firmenname">{firma?.firmenname || "Firmenportal"}</p>
              <p className="text-[11px] text-cyan-600 font-semibold tracking-wider uppercase">Schulungsportal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/schulungen" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-50"><Home className="h-4 w-4" />Schulungsseite</Link>
            <button onClick={handleLogout} data-testid="portal-logout-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-500 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"><LogOut className="h-4 w-4" />Abmelden</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {!firma ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>
        ) : (
          <>
            <div>
              <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900">Willkommen, {firma.ansprechpartner}</h1>
              <p className="text-slate-500 mt-1">Hier verwalten Sie Schulungsplätze, Einladungen und den Fortschritt Ihrer Mitarbeitenden.</p>
            </div>
            <Tabs value={tab} onValueChange={goTo}>
              <TabsList className="bg-white border border-slate-200/60 rounded-xl h-auto p-1 flex-wrap justify-start">
                <TabsTrigger value="uebersicht" className="rounded-lg px-4 py-2.5 data-[state=active]:bg-cyan-700 data-[state=active]:text-white" data-testid="tab-uebersicht"><LayoutDashboard className="h-4 w-4 mr-2" />Übersicht</TabsTrigger>
                <TabsTrigger value="bestellungen" className="rounded-lg px-4 py-2.5 data-[state=active]:bg-cyan-700 data-[state=active]:text-white" data-testid="tab-bestellungen"><Receipt className="h-4 w-4 mr-2" />Bestellungen</TabsTrigger>
                <TabsTrigger value="mitarbeiter" className="rounded-lg px-4 py-2.5 data-[state=active]:bg-cyan-700 data-[state=active]:text-white" data-testid="tab-mitarbeiter"><Users className="h-4 w-4 mr-2" />Mitarbeitende</TabsTrigger>
              </TabsList>
              <TabsContent value="uebersicht" className="mt-6"><Uebersicht firma={firma} teilnehmer={teilnehmer} goTo={goTo} /></TabsContent>
              <TabsContent value="bestellungen" className="mt-6"><FirmenBestellungen onChanged={load} /></TabsContent>
              <TabsContent value="mitarbeiter" className="mt-6"><FirmenEinladungen firma={firma} onChanged={load} /></TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
